import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { store, RootState } from '../app/store';
import UserService from "../Services/User/UserService";
import TeamService from "../Services/Team/TeamService";
import { SnackbarType } from '../constants/SnackbarType';
import { appDebugWarn } from '../Util/appDebugLog';

import { showSnackbar } from './snackbar';

const initialState: any = { 
  authUser: {}, 
  user: {}, 
  userTeam: {}, 
  teamWithOutGoingPreference: {}, 
  userWrapUpStatus: {}, 
  lastCallRecord: null,
};

/** Prefer profile id; fall back to JWT userId. Never return null/undefined/"null". */
function resolveAuthUserId(state: RootState): string | null {
  const id = state.auth?.user?.id ?? state.auth?.authUser?.userId;
  if (id == null || id === '' || id === 'null' || id === 'undefined') {
    return null;
  }
  return String(id);
}

export const getUser: any = createAsyncThunk(
  "getUser/get",
  async (userId: any, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const { tenantCode, AccessToken } = currentState.auth.authUser ?? {};
    const resolvedUserId = userId || resolveAuthUserId(currentState);
    if (!resolvedUserId) {
      appDebugWarn('getUser skipped — missing userId');
      return rejectWithValue('Missing userId');
    }
    const result = await UserService.getUser(resolvedUserId, tenantCode, AccessToken);

    return result.data;
  }
);

export const getUserTeam: any = createAsyncThunk(
  "getUserTeam/get",
  async (userId: any, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const { tenantCode, AccessToken } = currentState.auth.authUser ?? {};
    const resolvedUserId = userId || resolveAuthUserId(currentState);
    if (!resolvedUserId) {
      appDebugWarn('getUserTeam skipped — missing userId');
      return rejectWithValue('Missing userId');
    }
    const result = await UserService.getUserTeam(resolvedUserId, tenantCode, AccessToken);

    return result.data;
  }
);

export const updateUserStatus: any = createAsyncThunk(
  "updateUserStatus/put",
  async (userStatuses: any, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const { tenantCode, AccessToken } = currentState.auth.authUser ?? {};
    const userId = resolveAuthUserId(currentState);
    if (!userId) {
      appDebugWarn('updateUserStatus skipped — missing userId (would call /userstatus/null/...)');
      return rejectWithValue('Missing userId');
    }
    if (!tenantCode || !AccessToken) {
      appDebugWarn('updateUserStatus skipped — missing auth');
      return rejectWithValue('Missing auth');
    }
    const result = await UserService.updateUserAvailabilityStatus(userId, tenantCode, AccessToken, userStatuses);

    return result.data;
  }
);

export const setUserOutboundCallNumberState: any = createAsyncThunk(
  "setUserOutboundCallNumberState/post",
  async (outgoingCallNumberState: any, { getState }) => {
    const currentState = getState() as RootState;
    const { tenantCode, AccessToken } = currentState.auth.authUser ?? {};
    const userId = resolveAuthUserId(currentState);

    if (!userId) {
      appDebugWarn('setUserOutboundCallNumberState skipped — missing userId');
      const snackbarData = { message: 'Failed to update the dial out number.'};
      store.dispatch(showSnackbar({ snackbarType: SnackbarType.Error, snackbarData }));
      return;
    }

    try {
      const result = await UserService.setUserOutboundCallNumberState(userId, tenantCode, AccessToken, outgoingCallNumberState);
      
      return result.data;
    } catch (error) {
      const snackbarData = { message: 'Failed to update the dial out number.'};
      store.dispatch(showSnackbar({ snackbarType: SnackbarType.Error, snackbarData }));
    }
  }
);

export const getTeamWithOutGoingCallIdPreference: any = createAsyncThunk(
  "getTeamWithOutGoingCallIdPreference/get",
  async (_, { getState }) => {
    const currentState = getState() as RootState;
    const { tenantCode, AccessToken } = currentState.auth.authUser;
    const { id } = currentState.auth?.userTeam;
    const result = await TeamService.getTeam(id, tenantCode, AccessToken);

    return result.data;
  }
);

export const getUserWrapupStatus: any = createAsyncThunk(
  "getUserWrapupStatus/get",
  async (userId: any, { getState, rejectWithValue }) => {
    const currentState = getState() as RootState;
    const { tenantCode, AccessToken } = currentState.auth.authUser ?? {};
    const resolvedUserId = userId || resolveAuthUserId(currentState);
    if (!resolvedUserId) {
      appDebugWarn('getUserWrapupStatus skipped — missing userId');
      return rejectWithValue('Missing userId');
    }
    const result = await UserService.getUserWrapupStatus(resolvedUserId, tenantCode, AccessToken);

    return result.data;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    addAuthInfo(state, action: PayloadAction<any>) {
      state.authUser = action.payload;
    },
    addLastCallRecord(state, action: PayloadAction<any>) {
      state.lastCallRecord = action.payload;
    },
    updateUserAvailabilityStatus(state, action: PayloadAction<any>) {
      const { userStatus, available } = action.payload;
      state.user.userStatus = userStatus;
      state.user.available = available;
    },
    refreshToken(state, action: PayloadAction<any>) {
      const { accessToken, refreshToken, accessTokenExpiresInSeconds } = action.payload;

      state.authUser.AccessToken = accessToken;
      state.authUser.RefreshToken = refreshToken;
      state.authUser.AccessTokenExpiresInSeconds = accessTokenExpiresInSeconds;
    },
  },
  extraReducers: {
    // User
    [getUser.fulfilled]: (state, action) => {
      const { data } = action.payload;
      state.user = data;
    },
    [getUser.rejected]: (state, action) => {
      state.user.error = action.error;
    },

    // userTeam
    [getUserTeam.fulfilled]: (state, action) => {
      state.userTeam = action.payload.data?.[0];
    },
    [getUserTeam.rejected]: (state, action) => {
      state.userTeam.error = action.error;
    },

    // User status — keep last known status on failure/skip (do not overwrite with error object)
    [updateUserStatus.fulfilled]: (state, action) => {
      const { data } = action.payload;
      if (data?.userStatus) {
        state.user.userStatus = data.userStatus;
      }
    },
    [updateUserStatus.rejected]: () => {
      // Intentionally no-op: missing userId / network failures must not corrupt userStatus
    },

    // User Caller Id
    [setUserOutboundCallNumberState.fulfilled]: (state, action) => {
      // The thunk resolves with undefined when it skips or swallows a request error.
      const data = action.payload?.data;
      if (!data) return;
      if (!state.user.preferences) state.user.preferences = {};
      state.user.preferences.outgoingCallIdPreference = data?.preferences?.outgoingCallIdPreference;
    },
    [setUserOutboundCallNumberState.rejected]: (state, action) => {
      if (state.user.preferences) state.user.preferences.outgoingCallIdPreference = null;
    },
    // Team with outgoing call id preferences
    [getTeamWithOutGoingCallIdPreference.fulfilled]: (state, action) => {
      const { data } = action.payload;
      state.teamWithOutGoingPreference = data;
    },
    [getTeamWithOutGoingCallIdPreference.rejected]: (state, action) => {
      state.teamWithOutGoingPreference = action.error;
    },

    // User WrapUp Status
    [getUserWrapupStatus.fulfilled]: (state, action) => {
      const { data } = action.payload;
      state.userWrapUpStatus = data;
    },
    [getUserWrapupStatus.rejected]: (state, action) => {
      state.userWrapUpStatus.error = action.error;
    },
  } 
});

export const { addAuthInfo, addLastCallRecord, updateUserAvailabilityStatus, refreshToken } = authSlice.actions;
export default authSlice.reducer;
